import { Target, Compass, ShieldCheck, FileCheck, Scale } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { values, processSteps, timeline, heroImages } from "@/data/content";

const ChiSiamo = () => (
  <div data-testid="chi-siamo-page">
    <PageHero
      eyebrow="Chi Siamo"
      lines={["La nostra storia,", <span key="i" className="italic text-gold">il nostro metodo</span>]}
      description="Vobis Credit S.r.l. è specializzata nell'acquisto, nella gestione e nella valorizzazione di portafogli di crediti deteriorati (NPL) e UTP, al servizio di banche, intermediari finanziari, investitori e imprese."
      image={heroImages.chiSiamo}
    />

    {/* STORIA */}
    <section className="bg-white py-28" data-testid="storia-section">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <Reveal>
            <span className="font-display text-6xl italic text-gold/40">01</span>
            <h2 className="mt-4 font-display text-4xl leading-[1.08] text-navy sm:text-5xl">La storia</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 leading-relaxed text-navy/65">
              Vobis Credit nasce dall'incontro di professionisti con una lunga esperienza nella
              finanza, nel diritto e nella gestione immobiliare, uniti da una convinzione: i crediti
              deteriorati non sono un problema da smaltire, ma un patrimonio da valorizzare.
            </p>
            <p className="mt-5 leading-relaxed text-navy/65">
              Da quel primo nucleo è cresciuta una realtà strutturata, capace di seguire internamente
              l'intero ciclo di vita del credito: dall'analisi e selezione dei portafogli, all'acquisto,
              fino al servicing, al recupero e alla valorizzazione degli asset immobiliari sottostanti.
            </p>
            <p className="mt-5 leading-relaxed text-navy/65">
              Oggi Vobis Credit è il partner di riferimento per banche, intermediari finanziari,
              investitori e imprese che cercano un operatore affidabile, rapido e trasparente.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="relative">
          <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/10">
            <img src={heroImages.handshake} alt="Partnership Vobis Credit" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <div className="absolute -bottom-6 left-8 rounded-2xl bg-navy px-7 py-5 shadow-xl">
            <div className="font-display text-2xl text-gold">15+ anni</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/60">di esperienza del team</div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* MISSION & VISION */}
    <section className="bg-cream py-28" data-testid="mission-vision-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading eyebrow="Mission & Vision" title="Ciò che ci guida, ogni giorno" />
        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div data-testid="mission-card" className="group relative h-full overflow-hidden rounded-[2rem] bg-navy p-10 transition-transform duration-500 hover:-translate-y-1.5 lg:p-12">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-gold/15 blur-3xl transition-all duration-700 group-hover:bg-gold/25" />
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gold text-navy">
                <Target size={26} strokeWidth={1.8} />
              </span>
              <h3 className="mt-7 font-display text-3xl text-white">Mission</h3>
              <p className="mt-4 leading-relaxed text-white/65">
                Creare valore attraverso competenza, trasparenza e rapidità operativa: trasformiamo
                crediti deteriorati in opportunità concrete per chi ce li affida, gestendo ogni
                operazione con rigore e massima professionalità.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div data-testid="vision-card" className="group relative h-full overflow-hidden rounded-[2rem] border border-navy/10 bg-white p-10 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-2xl hover:shadow-navy/10 lg:p-12">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold">
                <Compass size={26} strokeWidth={1.8} />
              </span>
              <h3 className="mt-7 font-display text-3xl text-navy">Vision</h3>
              <p className="mt-4 leading-relaxed text-navy/60">
                Diventare il riferimento italiano per la gestione integrata dei crediti NPL e UTP:
                un operatore riconosciuto per affidabilità, innovazione e capacità di generare
                risultati misurabili per i propri partner.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* VALORI */}
    <section className="bg-white py-28" data-testid="valori-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading eyebrow="I Nostri Valori" title="I principi alla base di ogni operazione" />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div data-testid={`value-card-${i}`} className="group h-full rounded-3xl border border-navy/10 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-xl hover:shadow-navy/10">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold transition-all duration-500 group-hover:rotate-6 group-hover:bg-gold group-hover:text-navy">
                  <v.icon size={24} strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-bold text-navy">{v.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/55">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* METODO */}
    <section className="bg-cream py-28" data-testid="metodo-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading eyebrow="Il Nostro Processo" title="Un metodo strutturato in quattro fasi" />
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {processSteps.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.1}>
              <div data-testid={`process-step-${p.num}`} className="group h-full rounded-3xl bg-white p-8 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-navy/10">
                <span className="font-display text-5xl italic text-gold/50 transition-colors duration-500 group-hover:text-gold">{p.num}</span>
                <h3 className="mt-4 font-bold text-navy">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/55">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* TIMELINE */}
    <section className="bg-white py-28" data-testid="timeline-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading eyebrow="Il Percorso" title="Le tappe della nostra crescita" />
        <div className="relative mx-auto mt-20 max-w-5xl">
          <div className="absolute left-4 top-0 h-full w-px bg-gradient-to-b from-gold via-navy/15 to-transparent lg:left-1/2 lg:-translate-x-1/2" />
          {timeline.map((t, i) => (
            <Reveal key={t.year} className={`relative mb-16 pl-14 last:mb-0 lg:w-1/2 ${i % 2 ? "lg:ml-auto lg:pl-16" : "lg:pl-0 lg:pr-16 lg:text-right"}`}>
              <span className={`absolute top-2 h-3.5 w-3.5 rounded-full bg-gold ring-4 ring-gold/20 left-[9px] ${i % 2 ? "lg:-left-[7px]" : "lg:left-auto lg:-right-[7px]"}`} />
              <div data-testid={`timeline-item-${i}`}>
                <span className="font-display text-4xl italic text-gold">{t.year}</span>
                <h3 className="mt-2 text-xl font-bold text-navy">{t.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/60">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* AFFIDABILITÀ */}
    <section className="relative overflow-hidden bg-navy py-24" data-testid="affidabilita-section">
      <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading dark eyebrow="Esperienza & Affidabilità" title="Operiamo nel pieno rispetto della normativa" />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "Art. 115 T.U.L.P.S.", text: "Autorizzazioni previste dalla legge per l'attività di recupero crediti, nel pieno rispetto della normativa vigente." },
            { icon: FileCheck, title: "Artt. 114 e ss. T.U.B.", text: "Conformità, ove applicabile, alla disciplina del Testo Unico Bancario in materia di cessione e gestione dei crediti." },
            { icon: Scale, title: "Network legale specializzato", text: "Collaborazione con professionisti del diritto bancario e delle esecuzioni per le attività giudiziali." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div data-testid={`compliance-card-${i}`} className="h-full rounded-3xl border border-white/10 bg-white/[0.05] p-8 backdrop-blur-sm transition-colors duration-500 hover:border-gold/40">
                <c.icon size={26} className="text-gold" strokeWidth={1.8} />
                <h3 className="mt-5 font-bold text-white">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/55">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBand />
  </div>
);

export default ChiSiamo;
